import {
  NotificationType,
  NOTIFICATIONS_CONFIG,
  TASK_STATE_TO_NOTIFICATION_TYPE_MAP
} from '@/common/constants/notifications.constants';
import { useStorage } from '@vueuse/core';
import { TASK_MAP } from '@/common/store/modules/tasks/tasksNotificationMapper';
import Task from '@/common/models/task.model';
import uniqid from 'uniqid';
import Vue from 'vue';
import { castArray, merge, pick } from 'lodash-es';
import jsonParseSafe from '@/common/utils/jsonParseSafe';
import type { ElNotificationOptions } from '@uikit/ui-kit/types/notification';
import { getTimestamp } from '@/common/utils/date';
import { trackDebounced } from '@/common/utils/telemetry';

export type NotificationState = {
  version: number
  muted: object,
  maxBottomItems: number,
  maxMainItems: number,
  notifications: ElNotificationOptions[],
  requestedTasks: string[]
}

export const getInitialConfig = () => ({
  [NotificationType.ALL]: false,
  [NotificationType.ERROR]: false,
  [NotificationType.INFO]: false,
  [NotificationType.SUCCESS]: false
});

export const initialState: NotificationState = {
  version: 2, // changing version will reset the state
  muted: getInitialConfig(),
  maxBottomItems: 3,
  maxMainItems: 30,
  notifications: [],
  requestedTasks: []
};

const getUpdatedTimestamp = (notification, existingNotification) => {
  // TODO: provide finished timestamp for all tasks
  if (notification.task && existingNotification.type === notification.type) {
    try {
      return notification.task.finished ? getTimestamp(notification.task.finished) : existingNotification.timestamp;
    } catch (e) {
      console.error(e);
      return existingNotification.timestamp;
    }
  }
  return Date.now();
};

export const getInitialState = () => {
  const localStorageString = localStorage.getItem('notifications');
  const storage = localStorageString ? jsonParseSafe(localStorageString) as NotificationState : undefined;

  if (storage?.version !== initialState.version) {
    localStorage.removeItem('notifications');
  }

  return useStorage(
    'notifications',
    initialState,
    localStorage,
    {
      mergeDefaults: (storageValue, defaults) => merge(defaults, {
        ...storageValue,
        notifications: storageValue.notifications.filter(it => !it.skipStore && it.type !== NotificationType.PROGRESS)
      }),
      writeDefaults: false
    }
  ).value;
};

const notificationGetters: CommonGetterTree<NotificationState> = {
  doNotDisturb: state => state.muted[NotificationType.ALL],
  isMuted: state => type => state.muted[NotificationType.ALL] || state.muted[type],
  getItems: (state) => state.notifications.map(notification => {
    if (notification.task) {
      try {
        const taskModel = new Task(notification.task);
        const getConfig = TASK_MAP[taskModel.name]?.[taskModel.state];
        const config = getConfig && getConfig(taskModel);
        return {
          ...notification,
          ...config,
          hasMessage: !!config
        };
      } catch (error) {
        console.error('Error while processing task notification', error);
        return notification;
      }
    }
    return notification;
  }).slice().sort((a, b) => b.timestamp - a.timestamp),
  getItemById: state => id => state.notifications.find(it => it.id === id),
  tasksNotifications: (_state, getters) => getters.getItems
    .filter(it => it.task)
    .map(notification => ({
      ...notification,
      title: undefined,
      message: notification.task.name
    })),
  mainNotifications: (_state, getters) => getters.getItems.filter(
    it => it.task ? it.hasMessage : true
  ),
  bottomNotifications: (state, getters) => getters.getItems.filter(
    it => {
      const shouldShow = it.showBottom && !getters.isMuted(it.type);
      if (it.task) {
        return state.requestedTasks.includes(it.id) && it.hasMessage && shouldShow;
      }
      return shouldShow;
    }
  ),
  visibleBottomNotifications: (state, getters) => getters.bottomNotifications.slice(-1 * state.maxBottomItems),
  preventReloadProgressNotifications: (_state, getters) => getters.getItems.filter(it => it.type === NotificationType.PROGRESS && it.preventReload)
};

const notificationMutations: CommonMutationTree<NotificationState> = {
  ADD(state, notification) {
    state.notifications = [...state.notifications, notification].slice(-1 * state.maxMainItems);
  },
  REMOVE(state, id) {
    const ids = castArray(id);
    state.notifications = state.notifications.filter(notification => !ids.includes(notification.id));
  },
  UPDATE(state, newNotification) {
    const index = state.notifications.findIndex(notification => notification.id === newNotification.id);
    Vue.set(state.notifications, index, { ...state.notifications[index], ...newNotification });
  },
  MUTE(state, types) {
    Object.assign(state.muted, types);
  },
  FLUSH_STATE(state) {
    localStorage.removeItem('notifications');
    // eslint-disable-next-line unused-imports/no-unused-vars
    state = getInitialState();
  },
  ADD_TASK_ID(state, taskId) {
    const { requestedTasks } = state;
    if (!requestedTasks.includes(taskId)) {
      requestedTasks.push(taskId);
    }
  },
  REMOVE_TASK_IDS(state, id) {
    const ids = castArray(id);
    state.requestedTasks = state.requestedTasks.filter(taskId => !ids.includes(taskId));
  },
};

const notificationActions: CommonActionTree<NotificationState> = {
  PROCESS_WS_TASK({ dispatch }, task) {
    dispatch('ADD_OR_UPDATE', {
      id: task.task_id,
      operationId: task.task_id,
      type: TASK_STATE_TO_NOTIFICATION_TYPE_MAP[task.state],
      message: task.name,
      progress: task.progress,
      task
    });
  },
  REVEAL_TASK({ dispatch }, task) {
    dispatch('ADD_OR_UPDATE', {
      id: task.task_id,
      operationId: task.task_id,
      type: TASK_STATE_TO_NOTIFICATION_TYPE_MAP[task.state],
      message: task.name,
      progress: task.progress,
      // NOTE (mpetrov): hide bottom notification for task that was revealed by websocket
      showBottom: false,
      task
    });
  },
  ADD({ state, commit, getters }, notification) {
    const existingNotification = getters.getItemById(notification.id);
    if (existingNotification) return;

    const notificationType = notification.type || NotificationType.INFO;
    const progress = notification.type === NotificationType.PROGRESS ? notification.progress : undefined;
    const { task } = notification;
    const showBottom = notification.showBottom ?? (
      !getters.isMuted(notificationType)
      && (!task || state.requestedTasks.includes(task.task_id))
    );

    const timestamp = task?.finished ? getTimestamp(task.finished) : Date.now();

    const updatedNotification = {
      ...NOTIFICATIONS_CONFIG[notificationType],
      id: uniqid('notification_'),
      timestamp,
      isSeen: false,
      type: notificationType,
      components: [],
      ...notification,
      showBottom,
      progress
    };
    trackDebounced('notification', pick(updatedNotification, ['id', 'timestamp', 'type', 'operationId', 'title', 'message']));
    commit('ADD', updatedNotification);
  },
  UPDATE({ state, commit, getters }, notification) {
    const existingNotification = getters.getItemById(notification.id);
    if (!existingNotification) return;

    // NOTE (mpetrov): condition for showing bottom notification when working with tasks:
    // 1. notifications musn't be muted
    // 2. only initiator of the task bottom notifications must see bottom notification
    // 3. if type changes to success or fail, bottom notification must be shown
    // 4. otherwise preserve current showBottom value
    const notificationType = notification.type || existingNotification.type;
    const progress = notificationType === NotificationType.PROGRESS ? notification.progress : undefined;
    const isDifferentNotificationType = notification.task && existingNotification.type !== notification.type;
    const isMuted = getters.isMuted(notificationType)
      || (notification.task && !state.requestedTasks.includes(notification.task.task_id));
    const showBottom = !isMuted
      && (notification.showBottom ?? (isDifferentNotificationType || existingNotification.showBottom));

    const updatedNotification = {
      ...existingNotification,
      ...NOTIFICATIONS_CONFIG[notificationType],
      ...notification,
      showBottom,
      progress,
      timestamp: getUpdatedTimestamp(notification, existingNotification)
    };

    trackDebounced('notification', pick(updatedNotification, ['id', 'timestamp', 'type', 'operationId', 'title', 'message']));
    commit('UPDATE', updatedNotification);
  },
  ADD_OR_UPDATE({ getters, dispatch }, notification) {
    const existingNotification = getters.getItemById(notification.id);
    return existingNotification
      ? dispatch('UPDATE', notification)
      : dispatch('ADD', notification);
  },
  TRACK_TASK({ commit, state }, taskId) {
    commit('ADD_TASK_ID', taskId);

    // NOTE (mpetrov): race condition: if task was added to the store just before REST endpoint responded,
    // need to update showBottom value
    const notification = state.notifications.find(it => it.task && it.task.task_id === taskId);
    if (notification
      && !notification.showBottom
      && !notification.isSeen
      && notification.type === NotificationType.PROGRESS
    ) {
      commit('UPDATE', { id: notification.id, showBottom: true });
    }
  },
  REMOVE({ commit }, id) {
    commit('REMOVE', id);
    commit('REMOVE_TASK_IDS', id);
  },
  DISMISS({ commit, getters }, id) {
    const notification = getters.getItemById(id);
    if (!notification) return;

    commit('UPDATE', {
      id,
      showBottom: false
    });
  },
  SET_SEEN_NOTIFICATIONS({ state, commit }) {
    state.notifications.forEach(notification => commit('UPDATE', {
      id: notification.id,
      isSeen: notification.type !== NotificationType.PROGRESS
    }));
  },
  MUTE({ commit }, types) {
    commit('MUTE', types);
  },
  UNMUTE({ commit }) {
    commit('MUTE', getInitialConfig());
  }
};

export default new class implements CommonStore<NotificationState, unknown> {
  name = 'notifications';
  state = getInitialState();
  getters = notificationGetters;
  mutations = notificationMutations;
  actions = notificationActions;
}();
